import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type Theme = 'light' | 'dark';
export type ModalType =
  | 'service_selector'
  | 'recipe_selector'
  | 'recipe_editor'
  | 'recipe_preview'
  | 'room_delete_confirm'
  | 'recipe_delete_confirm';

interface CurrencyInfo {
  code: string;
  label: string;
}

export const CURRENCIES: CurrencyInfo[] = [
  { code: 'PLN', label: 'PLN (zł)' },
  { code: 'EUR', label: 'EUR (€)' },
];

interface UIState {
  theme: Theme;
  toggleTheme: () => void;
  currency: string;
  setCurrency: (code: string) => void;
  availableCurrencies: CurrencyInfo[];
  activeModal: ModalType | null;
  // IN-02 fix: use unknown instead of any — callers must narrow before reading.
  // CLAUDE.md rule 5: strict TS, no any.
  modalData: Record<string, unknown> | null;
  openModal: (type: ModalType, data?: Record<string, unknown> | null) => void;
  closeModal: () => void;
  showPersistenceWarning: boolean;
  setShowPersistenceWarning: (show: boolean) => void;
  /**
   * Whether the Materials tab is visible (MAT-04).
   * MUST default to false — labour-only users should never see the tab appear.
   * (CLAUDE.md rule 10: optional complexity off by default.)
   */
  materialsTabEnabled: boolean;
  setMaterialsTabEnabled: (enabled: boolean) => void;
  /**
   * DOC-03 gate — runtime layer of the two-layer legal gate.
   * MUST default to false. This is a deploy-time developer action, NOT a user toggle.
   * Production access requires BOTH this flag AND VITE_CONTRACTS_LEGAL_APPROVED=true.
   * A DevTools localStorage flip alone cannot unlock production (CLAUDE.md rule 10).
   */
  contractsLegalApproved: boolean;
  setContractsLegalApproved: (approved: boolean) => void;
  /**
   * ONBOARD-01: hasExported tracks whether the user exported in the current session.
   * Used by useOnboarding step 3 detection. Intentionally NOT persisted — must reset
   * to false on page reload so the step reflects current-session export action, not
   * historical. Set true at BOTH export sites (Excel + PDF) via setHasExported(true).
   */
  hasExported: boolean;
  setHasExported: (value: boolean) => void;
}

/**
 * Exported migrate function for testing without Zustand's internal plumbing.
 * Bumps version 1 → 2: adds materialsTabEnabled: false for existing users.
 */
export function migrateUIStore(persistedState: unknown, version: number): unknown {
  let state = persistedState as object;
  if (version < 2) {
    state = { ...state, materialsTabEnabled: false };
  }
  if (version < 3) {
    state = { ...state, contractsLegalApproved: false };
  }
  return state;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      theme: (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) ? 'dark' : 'light',
      toggleTheme: () => set((state) => ({ theme: state.theme === 'light' ? 'dark' : 'light' })),
      currency: 'PLN',
      setCurrency: (code: string) => set({ currency: code }),
      availableCurrencies: CURRENCIES,
      activeModal: null,
      modalData: null,
      openModal: (type, data = null) => set({ activeModal: type, modalData: data }),
      closeModal: () => set({ activeModal: null, modalData: null }),
      showPersistenceWarning: false,
      setShowPersistenceWarning: (show) => set({ showPersistenceWarning: show }),
      // MAT-04: materials tab off by default — BLOCKING (CLAUDE.md rule 10)
      materialsTabEnabled: false,
      setMaterialsTabEnabled: (enabled) => set({ materialsTabEnabled: enabled }),
      // DOC-03: contracts legal gate off by default — deploy-time developer action (CLAUDE.md rule 10)
      contractsLegalApproved: false,
      setContractsLegalApproved: (approved) => set({ contractsLegalApproved: approved }),
      // ONBOARD-01: hasExported intentionally NOT in partialize — resets on reload (see interface)
      hasExported: false,
      setHasExported: (value) => set({ hasExported: value }),
    }),
    {
      name: 'estimatix-ui',
      version: 3,
      storage: createJSONStorage(() => localStorage),
      migrate: migrateUIStore,
      partialize: (state) => ({
        theme: state.theme,
        currency: state.currency,
        materialsTabEnabled: state.materialsTabEnabled,
        // WR-04 fix: contractsLegalApproved intentionally NOT persisted.
        // It resets to false on every reload — the env var (VITE_CONTRACTS_LEGAL_APPROVED)
        // is the authoritative backstop. Persisting it allowed DevTools localStorage flips
        // to survive reloads, undermining the deploy-time-only design intent (DOC-03, §Pitfall 4).
      }),
    },
  ),
);
